import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, MapPin, ArrowRight, Building2, Search } from "lucide-react";
import { countryGeoState, countryGeoCity, API } from "@/lib/api";

const VMAP = { "Restaurants & Cafes": "restaurant", "Restaurants & Food": "restaurant", "Hotels & Homestays": "hotel", "Hotels & Resorts": "hotel", "Tourism & Resorts": "hotel", "Hospitals & Clinics": "doctor", "Healthcare & Clinics": "doctor", "Healthcare": "doctor", "Retail & Boutiques": "retail", "Education & Schools": "school", "Education": "school", "Real Estate": "realtor" };

export default function IndiaGeo() {
  const { state, city } = useParams();
  const [data, setData] = useState(undefined);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    setData(undefined);
    const p = city ? countryGeoCity("in", state, city) : countryGeoState("in", state);
    p.then((d) => { setData(d); document.title = `WebsiteTech India · ${d.name}`; }).catch(() => setData(null));
    // contextual promo
    const qs = new URLSearchParams({ country: "in", state: state || "", city: city || "" });
    fetch(`${API}/marketing-board?${qs.toString()}`).then((r) => r.ok ? r.json() : null).then(setPromo).catch(() => {});
  }, [state, city]);

  if (data === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;
  if (data === null) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="india-geo-404">
      <MapPin className="text-slate-300" size={40} />
      <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">We're not in this location yet</h1>
      <p className="text-slate-500 mt-2">WebsiteTech India serves businesses everywhere — get a quote and we'll help.</p>
      <Link to="/in/contact" className="mt-6 bg-brand text-white font-semibold rounded-xl px-6 py-3">Get a quote</Link>
    </div>
  );

  const place = data.name;
  const stateName = city ? state.charAt(0).toUpperCase() + state.slice(1) : null;

  return (
    <div data-testid="india-geo">
      <section className="bg-gradient-to-br from-[#0B203E] to-[#071428] text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-1.5 text-blue-200 text-sm mb-3"><MapPin size={15} /> WebsiteTech India{stateName ? ` · ${stateName}` : ""}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold">Websites, AI & Digital Growth in <span className="text-blue-300">{place}</span></h1>
          <p className="text-blue-100/80 mt-4 md:text-lg max-w-2xl">{data.tagline}</p>
          <p className="text-blue-100/60 text-sm mt-3">{data.stat}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/in/audit" className="bg-white text-[#0B203E] font-semibold rounded-xl px-6 py-3.5 inline-flex items-center gap-2"><Search size={16} /> Free Health Check</Link>
            <Link to={`/in/contact?state=${encodeURIComponent(place)}`} className="bg-white/10 border border-white/20 rounded-xl px-6 py-3.5">Get a Quote</Link>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-slate-600 md:text-lg leading-relaxed">{data.intro}</p>

          {promo && (
            <div className="mt-10 rounded-2xl p-6 md:p-7 text-white flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between" style={{ background: "linear-gradient(135deg,#0B203E,#1e3a6d)" }} data-testid="india-geo-board">
              <div>
                <p className="font-heading text-xl font-bold">{promo.title}</p>
                <p className="text-blue-100/80 text-sm mt-1">{promo.subtitle}</p>
                {promo.location && <span className="inline-block mt-2 text-xs bg-white/15 rounded-full px-3 py-1">{promo.location}</span>}
              </div>
              <Link to="/in/services" className="bg-white text-[#0B203E] font-semibold rounded-xl px-5 py-3 whitespace-nowrap">{promo.cta_label}</Link>
            </div>
          )}

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-12 mb-5">Popular industries in {place}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data.industries || []).map((ind) => (
              <Link key={ind} to={`/in/contact?vertical=${VMAP[ind] || "other"}&${city ? "city" : "state"}=${encodeURIComponent(place)}`}
                data-testid="india-geo-industry" className="group bg-slate-50 border border-slate-200 rounded-xl p-5 hover:-translate-y-1 transition-transform">
                <Building2 size={18} className="text-brand mb-2" />
                <p className="font-semibold text-slate-900">{ind}</p>
                <span className="text-xs font-semibold text-brand inline-flex items-center gap-1 mt-2 group-hover:gap-2 transition-all">Get a website <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>

          {data.cities && data.cities.length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-5">Cities we serve in {place}</h2>
              <div className="flex flex-wrap gap-3">
                {data.cities.map((c) => (
                  <Link key={c.slug} to={`/in/${state}/${c.slug}`} data-testid="india-geo-city-link"
                    className="bg-white border border-slate-200 rounded-xl px-5 py-3 card-shadow hover:border-brand/40 transition-colors">
                    <span className="font-semibold text-slate-900">{c.name}</span>
                    <span className="block text-xs text-slate-400">{c.tagline}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-14 text-center bg-slate-50 rounded-2xl p-8">
            <h3 className="font-heading text-xl font-bold text-slate-900">Grow your {place} business online</h3>
            <p className="text-slate-500 mt-2">See ₹ pricing or get a tailored proposal.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <Link to="/in/pricing" className="bg-brand text-white font-semibold rounded-xl px-6 py-3">See ₹ Pricing</Link>
              <Link to={`/in/contact?${city ? "city" : "state"}=${encodeURIComponent(place)}`} className="bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl px-6 py-3">Get a Quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
